import { useEffect } from 'react';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import { SerpSeasonPlanTablesView } from '@/components/Approval/@components/SerpSeasonPlan';
import { getSerpSeasonPlanDraftSourceUrl } from '@/constant/approval';
import { getNestedDataFetcher } from '@/utils/apis';
import type { SerpDocumentRef } from '@/utils/approval/serpDocument';
import type { SerpSeasonPlanDraftSource } from '@/utils/approval/serpSeasonPlan';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

type SerpSeasonPlanFieldProps = {
  /** 신규 작성은 진입 쿼리, 편집은 저장 attributes 의 SERP 문서 */
  serpDocument: SerpDocumentRef | null;
};

/**
 * SERP 시즌운영계획(159) — 레거시 appr_edit_forms_serp_season.
 * 제목은 직접 입력, 편집도 SERP 를 다시 조회해 표를 새로 그림
 */
const SerpSeasonPlanField = ({ serpDocument }: SerpSeasonPlanFieldProps) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'serpSeasonPlan' });

  const typeCode = serpDocument?.typeCode;
  const sequence = serpDocument?.sequence;
  const { data, error, isLoading } = useSWR<SerpSeasonPlanDraftSource>(
    typeCode && sequence
      ? getSerpSeasonPlanDraftSourceUrl(typeCode, sequence)
      : null,
    getNestedDataFetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  useEffect(() => {
    const current = getValues('serpSeasonPlan');
    if (
      current &&
      (current.typeCode !== typeCode || current.sequence !== sequence)
    ) {
      setValue('serpSeasonPlan', null, { shouldDirty: false });
    }
  }, [typeCode, sequence, getValues, setValue]);

  useEffect(() => {
    if (!data || !typeCode || !sequence) return;
    if (
      data.attributes.typeCode !== typeCode ||
      data.attributes.sequence !== sequence
    )
      return;
    setValue(
      'serpSeasonPlan',
      { typeCode, sequence, source: data.display.source ?? {} },
      { shouldDirty: false },
    );
  }, [data, typeCode, sequence, setValue]);

  const renderBody = () => {
    if (!serpDocument) {
      return (
        <ApprovalNoticeBox role="alert">
          {t('SERP 문서 정보가 없습니다.')}
        </ApprovalNoticeBox>
      );
    }
    if (state?.source) {
      return <SerpSeasonPlanTablesView source={state.source} t={t} />;
    }
    if (error) {
      return (
        <ApprovalNoticeBox role="alert">
          {t('데이터를 불러오지 못했습니다.')}
        </ApprovalNoticeBox>
      );
    }
    if (isLoading) {
      return (
        <ApprovalNoticeBox role="status">
          {t('데이터를 불러오는 중입니다...')}
        </ApprovalNoticeBox>
      );
    }
    return null;
  };

  return <FieldBlock className="serp-season-plan">{renderBody()}</FieldBlock>;
};

export default SerpSeasonPlanField;
